import { useCallback, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import { TableToggleFullScreen } from "../utils/TableToggleFullScreen";
import { TableExcelExport } from "../utils/TableExcelExport";
import { _COLORS } from "../../utils/constants";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  CellSelectionModule,
  ExcelExportModule,
  RowGroupingModule,
  RowGroupingPanelModule,
} from "ag-grid-enterprise";
import { columnBuilder } from "../../utils/AgGridUtils/ColumnBuilders/columnBuilder";
import CustomGroupHeader from "../../utils/AgGridUtils/CustomGroupHeader";
import { parseInternationalNumber } from "../budget/BaseBudgetSaveFunctions";

ModuleRegistry.registerModules([
  AllCommunityModule,
  ExcelExportModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  CellSelectionModule,
]);

/**
 *
 * @param {Object[]} rows - Array of row data to be displayed in the table.
 * @param {Object[]} columns - Array of column definitions for the table.
 * @param {Object[]} [columnsAfterMonths=[]] - Array of column definitions to be displayed after the months.
 * @param {boolean} [columnsIncludeMonths=true] - If true, the columns will include months.
 * @param {Object} [columnsMonthAdditionalArgs={}] - Additional arguments to be passed to the month columns.
 * @param {Function} onCellValueChanged - Callback function to handle cell value changes.
 * @param {Object[]} excelStyles - Array of styles to be applied when exporting to Excel.
 * @param {number} [height=450] - Height of the table container.
 * @param {number} [rowHeight=50] - Height of each row in the table.
 * @param {boolean} [fullHeight=false] - If true, the table will adjust its height automatically.
 * @param {boolean} [showExport=true] - If true, the export button will be displayed.
 * @param {boolean} [showFullScreen=true] - If true, the fullscreen toggle button will be displayed.
 * @param {Object} [agGridProps={}] - Additional properties to be passed to the AgGridReact component.
 * @param {Object} [gridRefProps] - Reference to the AgGridReact component.
 * @param {boolean} [isDataLoading=false] - If true, a loading spinner will be displayed
 * @param {string} [fileNameCsvExport="export"] - The name of the CSV file to be exported.
 *
 * @returns {JSX.Element} The rendered DataTable component.
 */
export const DataTable = ({
  rows,
  columns,
  columnsAfterMonths = [],
  columnsIncludeMonths = false,
  columnsMonthAdditionalArgs = {},
  onCellValueChanged,
  excelStyles = [],
  height = "auto",
  rowHeight = 25,
  fullHeight = true,
  showExport = true,
  showFullScreen = true,
  agGridProps = {},
  isDataLoading = false,
  gridRef,
  fileNameCsvExport = "export",
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const apiRef = useRef(null);

  const handleCellValueChanged = useCallback(
    (params) => {
      if (onCellValueChanged) {
        onCellValueChanged(params);
      }
    },
    [onCellValueChanged],
  );

  // Extraire le onGridReady externe pour éviter qu'il écrase le nôtre
  const { onGridReady: externalOnGridReady, ...restAgGridProps } = agGridProps;

  const composedOnGridReady = useCallback(
    (params) => {
      // Handler interne
      apiRef.current = params.api;

      // Appel du handler externe si fourni
      if (typeof externalOnGridReady === "function") {
        externalOnGridReady(params);
      }
    },
    [externalOnGridReady],
  );

  // Ajouter le parser international aux colonnes de mois si elles ne l'ont pas déjà
  const enhancedColumnsMonthAdditionalArgs = {
    ...columnsMonthAdditionalArgs,
    valueParser:
      columnsMonthAdditionalArgs.valueParser ||
      ((params) => parseInternationalNumber(params.newValue)),
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
        {showFullScreen && (
          <TableToggleFullScreen
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
          />
        )}
        {showExport && (
          <TableExcelExport
            gridRef={apiRef}
            fileNameCsvExport={fileNameCsvExport}
          />
        )}
      </Box>
      <Box id={"grid-container"} style={{ height, width: "100%" }}>
        <AgGridReact
          // Props externes d'abord (sans onGridReady)
          {...restAgGridProps}
          // Puis nos props contrôlées
          loading={isDataLoading}
          ref={gridRef}
          rowData={rows}
          onGridReady={composedOnGridReady}
          suppressClipboardPaste={false}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          columnDefs={columnBuilder(
            columns,
            columnsAfterMonths,
            columnsIncludeMonths,
            enhancedColumnsMonthAdditionalArgs,
          )}
          excelStyles={[
            {
              id: "month-header",
              interior: {
                color: _COLORS.headerAgGrid,
                pattern: "Solid",
              },
            },
            ...(Array.isArray(excelStyles) ? excelStyles : []),
          ]}
          onCellValueChanged={handleCellValueChanged}
          rowHeight={rowHeight}
          domLayout={fullHeight ? "autoHeight" : "normal"}
          cellSelection={true}
          groupDisplayType="groupRows"
          groupDefaultExpanded={-1}
          groupRowRenderer={(node) => (
            <CustomGroupHeader
              node={node}
              style={{ color: _COLORS.groupTitleColor }}
            />
          )}
        />
      </Box>
    </>
  );
};
